import { Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { SpriteIcon } from '@/components/SpriteIcon';
import { useUserDetails } from './useUsers';

/**
 * Read-only view of one account, behind the eye button on each row.
 *
 * The layout is the approved one: `viewUserModal` in `Users.cshtml` plus the
 * body `users-admin.js` built into it — a wide dialog, a `dl.row` of
 * three-column labels against nine-column values, and status and roles as
 * badges rather than text. Reproduced rather than redesigned, because this is a
 * screen the client has already signed off.
 *
 * Fetched per user rather than read off the grid row: `GET /api/users/{id}`
 * returns fields the list projection leaves out, and it is only wanted for the
 * row actually opened.
 */
export interface UserDetailsModalProps {
  userId: string;
  onClose: () => void;
}

/**
 * Dates render in the viewer's locale, as `toLocaleString` did in the Razor
 * version. These are display-only — unlike the Takata grids, nothing here is
 * filtered by date, so there is no round trip for a zone shift to break.
 */
function formatDateTime(value: string | null | undefined): string | null {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed.toLocaleString();
}

export function UserDetailsModal({ userId, onClose }: UserDetailsModalProps) {
  const { t } = useTranslation();
  const { data: user, isLoading, isError } = useUserDetails(userId);

  const fullName = user ? [user.firstName, user.lastName].filter(Boolean).join(' ').trim() : '';
  const lastLogin = formatDateTime(user?.lastLoginDate);

  return (
    <Modal show onHide={onClose} centered size="lg">
      <Modal.Header closeButton>
        {/* Flex row because the theme sets `.sa-icon { display: block }`, so an
            inline sprite in a heading pushes its own text onto a second line. */}
        <Modal.Title as="h5" className="d-flex align-items-center">
          <SpriteIcon name="user" size={20} className="sa-icon me-2" />
          {t('Users.Details', 'User details')}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {isLoading && (
          <div className="text-center py-4">
            <span className="spinner-border spinner-border-sm me-2" role="status" />
            {t('Loading', 'Loading...')}
          </div>
        )}

        {isError && (
          <div className="alert alert-danger mb-0" role="alert">
            {t('Users.DetailsError', 'Could not load this user.')}
          </div>
        )}

        {user && (
          <dl className="row mb-0">
            <dt className="col-sm-3">{t('Users.UserName', 'User name')}:</dt>
            <dd className="col-sm-9">
              <strong>{user.userName}</strong>
            </dd>

            <dt className="col-sm-3">{t('Users.Email', 'Email')}:</dt>
            <dd className="col-sm-9">{user.email}</dd>

            <dt className="col-sm-3">{t('Users.FullName', 'Full name')}:</dt>
            <dd className="col-sm-9">{fullName || t('NotAvailable', 'N/A')}</dd>

            <dt className="col-sm-3">{t('Users.Status', 'Status')}:</dt>
            <dd className="col-sm-9">
              {user.isActive ? (
                <span className="badge bg-success">{t('Users.Status.active', 'Active')}</span>
              ) : (
                <span className="badge bg-secondary">{t('Users.Status.inactive', 'Inactive')}</span>
              )}
              {/* Additive, not exclusive: an account can be active and locked at
                  once, and the Razor body showed both badges together. */}
              {user.isLockedOut && (
                <span className="badge bg-danger ms-2">{t('Users.Status.locked', 'Locked')}</span>
              )}
              {user.emailConfirmed ? (
                <span className="badge bg-info ms-2">
                  {t('Users.EmailVerified', 'Email verified')}
                </span>
              ) : (
                <span className="badge bg-warning ms-2">
                  {t('Users.EmailNotVerified', 'Email not verified')}
                </span>
              )}
            </dd>

            <dt className="col-sm-3">{t('Profile.TwoFactor', 'Two-step verification')}:</dt>
            <dd className="col-sm-9">
              {user.twoFactorEnabled ? (
                <span className="badge bg-success">{t('auth:Auth.MFA.Enabled', 'On')}</span>
              ) : (
                <span className="badge bg-secondary">{t('auth:Auth.MFA.Disabled', 'Off')}</span>
              )}
            </dd>

            <dt className="col-sm-3">{t('Users.Roles', 'Roles')}:</dt>
            <dd className="col-sm-9">
              {user.roles.length > 0 ? (
                user.roles.map((role) => (
                  <span className="badge bg-primary me-1" key={role}>
                    {role}
                  </span>
                ))
              ) : (
                <span className="text-muted">{t('Users.NoRoles', 'No roles assigned')}</span>
              )}
            </dd>

            <dt className="col-sm-3">{t('Users.Access.Title', 'Data access')}:</dt>
            <dd className="col-sm-9">
              {user.accessScopes.length > 0 ? (
                <span className="d-inline-flex flex-wrap gap-1">
                  {user.accessScopes.map((scope) => (
                    <span
                      className={`badge ${scope.scopeType === 'Country' ? 'bg-info' : 'bg-secondary'}`}
                      key={`${scope.scopeType}-${scope.scopeValue}`}
                    >
                      {scope.scopeType === 'Country'
                        ? t('Users.Access.Country', 'Country')
                        : t('Users.Access.Dealer', 'Dealer')}
                      : {scope.scopeValue}
                    </span>
                  ))}
                </span>
              ) : (
                /* Not the same as "no access" for every account: an Admin sees
                   everything from the role and never has a rule. */
                <span className="text-muted">
                  {t('Users.Access.None', 'No scope rules — sees no data unless a role grants it')}
                </span>
              )}
            </dd>

            <dt className="col-sm-3">{t('Users.Created', 'Created')}:</dt>
            <dd className="col-sm-9">{formatDateTime(user.createdDate) ?? '—'}</dd>

            <dt className="col-sm-3">{t('Users.LastLogin', 'Last login')}:</dt>
            <dd className="col-sm-9 mb-0">{lastLogin ?? t('Users.NeverLoggedIn', 'Never')}</dd>
          </dl>
        )}
      </Modal.Body>

      <Modal.Footer>
        <button type="button" className="btn btn-secondary" onClick={onClose}>
          {t('Close', 'Close')}
        </button>
      </Modal.Footer>
    </Modal>
  );
}
